import { useQuery } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Callout,
  Drawer,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonRows,
  Stat,
} from '../../components/ui';
import { api, qs } from '../../lib/api';
import { formatDateTime, humanise } from '../../lib/format';

/**
 * CILT compliance analytics.
 *
 * Two directives shape this page. §21: every number must be traceable down to
 * the records behind it, so each outcome count opens the individual scheduled
 * windows it came from. §27: a machine with nothing scheduled shows "No CILT
 * scheduled", never 0% — a rate of zero reads as total failure, which is the
 * opposite of what an empty schedule means.
 */

type Outcome = 'COMPLETED' | 'LATE' | 'PARTIAL' | 'MISSED' | 'PENDING';

interface Breakdown {
  key: string;
  label: string;
  sublabel: string | null;
  completed: number;
  late: number;
  partial: number;
  missed: number;
  pending: number;
  accountable: number;
  rate: number | null;
}

interface CiltResponse {
  range: { from: string; to: string };
  summary: Breakdown & { source: string; scheduled: number };
  breakdowns: Record<string, Breakdown[]>;
  calculation: string;
}

interface WindowRow {
  scheduleId: string;
  dueAt: string;
  dueBy: string;
  outcome: Outcome;
  machineCode: string;
  machineName: string;
  templateCode: string;
  templateName: string;
  lineName: string;
  shift: string | null;
  runShift: string | null;
  runId: string | null;
  runStatus: string | null;
  lateByMinutes: number | null;
  operatorName: string | null;
  abnormalCount: number;
}

const DIMENSIONS = [
  { value: 'machine', label: 'By machine' },
  { value: 'line', label: 'By line' },
  { value: 'template', label: 'By checklist' },
  { value: 'operator', label: 'By operator' },
  { value: 'shift', label: 'By shift' },
  { value: 'day', label: 'By day' },
  { value: 'week', label: 'By week' },
  { value: 'month', label: 'By month' },
];

const OUTCOME_TONE: Record<Outcome, 'ok' | 'warn' | 'alarm' | 'neutral'> = {
  COMPLETED: 'ok',
  LATE: 'warn',
  PARTIAL: 'warn',
  MISSED: 'alarm',
  PENDING: 'neutral',
};

const OUTCOME_LABEL: Record<Outcome, string> = {
  COMPLETED: 'Completed',
  LATE: 'Late',
  PARTIAL: 'Partially completed',
  MISSED: 'Missed',
  PENDING: 'Not yet due',
};

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

/** The pilot target from the proposal. Displayed as a target, never as an actual. */
const COMPLIANCE_TARGET = 95;

export function CiltAnalyticsPage() {
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [dimension, setDimension] = useState('machine');
  const [machineId, setMachineId] = useState('');
  const [drill, setDrill] = useState<{ outcome: Outcome; scope?: Breakdown } | null>(null);

  const range = {
    from: `${from}T00:00:00.000Z`,
    to: `${to}T23:59:59.999Z`,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['analytics', 'cilt', range.from, range.to, dimension, machineId],
    queryFn: () =>
      api.get<CiltResponse>(
        `/api/analytics/cilt${qs({ ...range, by: dimension, machineId: machineId || undefined })}`,
      ),
  });

  const { data: machines } = useQuery({
    queryKey: ['machines', 'picker'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; code: string; name: string }> }>('/api/machines'),
  });

  const { data: windows, isLoading: windowsLoading } = useQuery({
    enabled: drill !== null,
    queryKey: ['analytics', 'cilt', 'windows', range.from, range.to, drill?.outcome, drill?.scope?.key, machineId],
    queryFn: () =>
      api.get<{ data: WindowRow[] }>(
        `/api/analytics/cilt/windows${qs({
          ...range,
          outcome: drill?.outcome,
          machineId:
            // Drilling into a "by machine" row scopes to that machine; any
            // other dimension keeps whatever the page filter already had.
            dimension === 'machine' && drill?.scope ? drill.scope.key : machineId || undefined,
        })}`,
      ),
  });

  const summary = data?.summary;
  const rows = data?.breakdowns[dimension] ?? [];

  return (
    <div>
      <PageHeader
        title="CILT compliance"
        subtitle="Scheduled cleaning, inspection, lubrication and tightening — what was done, and when."
      />

      <div className="space-y-4 p-4 lg:p-6">
        <Card className="p-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="From">
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
            <Field label="Machine">
              <Select value={machineId} onChange={(e) => setMachineId(e.target.value)}>
                <option value="">All machines</option>
                {machines?.data.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.code} — {m.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Break down">
              <Select value={dimension} onChange={(e) => setDimension(e.target.value)}>
                {DIMENSIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </Card>

        {isLoading ? (
          <SkeletonRows rows={4} />
        ) : !summary ? null : summary.accountable === 0 ? (
          <Callout tone="info" title="No CILT scheduled in this range">
            Nothing was due, so there is no compliance figure to report. This is not 0% — a machine
            with no schedule is not a machine failing its checks.
          </Callout>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
              <Stat
                label="Compliance"
                value={`${summary.rate}%`}
                tone={
                  summary.rate === null
                    ? 'neutral'
                    : summary.rate >= COMPLIANCE_TARGET
                      ? 'ok'
                      : summary.rate >= 80
                        ? 'warn'
                        : 'alarm'
                }
                hint={`Target ≥${COMPLIANCE_TARGET}% · ${humanise(summary.source)}`}
              />
              {(['COMPLETED', 'LATE', 'PARTIAL', 'MISSED'] as const).map((outcome) => {
                const value =
                  outcome === 'COMPLETED'
                    ? summary.completed
                    : outcome === 'LATE'
                      ? summary.late
                      : outcome === 'PARTIAL'
                        ? summary.partial
                        : summary.missed;
                return (
                  <button key={outcome} type="button" onClick={() => setDrill({ outcome })}>
                    <Stat
                      label={OUTCOME_LABEL[outcome]}
                      value={value}
                      tone={value === 0 ? 'neutral' : OUTCOME_TONE[outcome]}
                      hint="Click to see the windows"
                    />
                  </button>
                );
              })}
            </div>

            {/* The rule is on the page, not in a doc nobody opens (directive §21). */}
            <Callout tone="info" title="How this is calculated">
              {data?.calculation}
              {summary.pending > 0
                ? ` ${summary.pending} window(s) in this range have not closed yet and are excluded.`
                : ''}
            </Callout>

            <Card>
              <CardHeader
                title={DIMENSIONS.find((d) => d.value === dimension)?.label ?? 'Breakdown'}
                subtitle="Worst compliance first. Every cell opens the windows behind it."
              />
              {rows.length === 0 ? (
                <EmptyState
                  icon={CalendarClock}
                  title="Nothing scheduled"
                  message="No CILT window falls in this range for the current filter."
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="border-b border-steel-200 text-left text-xs tracking-wide text-steel-500 uppercase">
                        <th className="px-4 py-2 font-semibold">
                          {dimension === 'operator' ? 'Operator' : 'Group'}
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">Completed</th>
                        <th className="px-3 py-2 text-right font-semibold">Late</th>
                        <th className="px-3 py-2 text-right font-semibold">Partial</th>
                        <th className="px-3 py-2 text-right font-semibold">Missed</th>
                        <th className="px-4 py-2 text-right font-semibold">Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-steel-100">
                      {rows.map((row) => (
                        <tr key={row.key} className="hover:bg-steel-50">
                          <td className="px-4 py-2">
                            <span className="font-semibold text-steel-900">{row.label}</span>
                            {row.sublabel ? (
                              <span className="block text-xs text-steel-500">{row.sublabel}</span>
                            ) : null}
                          </td>
                          {(['completed', 'late', 'partial', 'missed'] as const).map((field) => (
                            <td key={field} className="px-3 py-2 text-right tabular-nums">
                              {row[field] === 0 ? (
                                <span className="text-steel-400">0</span>
                              ) : (
                                <button
                                  type="button"
                                  className="font-semibold text-info-700 hover:underline"
                                  onClick={() =>
                                    setDrill({
                                      outcome: field.toUpperCase() as Outcome,
                                      scope: row,
                                    })
                                  }
                                >
                                  {row[field]}
                                </button>
                              )}
                            </td>
                          ))}
                          <td className="px-4 py-2 text-right">
                            {row.rate === null ? (
                              <span className="text-xs text-steel-500">No data</span>
                            ) : (
                              <Badge
                                tone={
                                  row.rate >= COMPLIANCE_TARGET
                                    ? 'ok'
                                    : row.rate >= 80
                                      ? 'warn'
                                      : 'alarm'
                                }
                              >
                                {row.rate}%
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}
      </div>

      <Drawer
        open={drill !== null}
        onClose={() => setDrill(null)}
        title={drill ? `${OUTCOME_LABEL[drill.outcome]} windows` : ''}
        subtitle={
          drill?.scope
            ? `${drill.scope.label} · ${from} to ${to}`
            : `All machines in range · ${from} to ${to}`
        }
        width="lg"
      >
        {windowsLoading ? (
          <SkeletonRows rows={5} />
        ) : !windows?.data.length ? (
          <EmptyState
            icon={CalendarClock}
            title="No windows"
            message="Nothing matches this outcome in the current range."
          />
        ) : (
          <ul className="divide-y divide-steel-100">
            {windows.data.map((row) => (
              <li key={`${row.scheduleId}-${row.dueAt}`} className="py-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-sm font-semibold text-steel-900">
                    {row.machineCode} · {row.templateCode}
                  </p>
                  <Badge tone={OUTCOME_TONE[row.outcome]}>{OUTCOME_LABEL[row.outcome]}</Badge>
                </div>
                <p className="mt-0.5 text-xs text-steel-600">
                  Due {formatDateTime(row.dueAt)} · window closed {formatDateTime(row.dueBy)}
                </p>
                <p className="mt-0.5 text-xs text-steel-500">
                  {row.shift ? `Shift ${row.shift}` : 'Any shift'}
                  {row.operatorName ? ` · ${row.operatorName}` : ' · nobody performed it'}
                  {row.lateByMinutes !== null ? ` · started ${row.lateByMinutes} min late` : ''}
                </p>
                {row.runId ? (
                  <Link
                    to={`/checklists/${row.runId}/review`}
                    className="mt-1 inline-block text-xs font-semibold text-info-700 hover:underline"
                  >
                    Open the run ({humanise(row.runStatus ?? '')})
                  </Link>
                ) : (
                  <p className="mt-1 text-xs text-steel-400">No run was recorded against it.</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Drawer>
    </div>
  );
}
